const FULL_MATCH_DURATION_MS = 10_800_000; // 3 h safety window

/**
 * Parse API matchDate as an absolute instant.
 * Common API shape: "Wed, 31 Dec 2025 21:00:00 GMT" (UTC).
 * Also supports ISO strings; bare ISO datetimes are treated as UTC.
 */
export function parseUtcMatchDate(matchDate?: string): Date | null {
  if (matchDate == null || matchDate === '') return null;

  const raw = String(matchDate).trim();
  if (!raw) return null;

  // RFC / HTTP dates from the API, e.g. "Wed, 31 Dec 2025 21:00:00 GMT"
  if (/\b(GMT|UTC)\b/i.test(raw)) {
    const gmtDate = new Date(raw);
    if (!Number.isNaN(gmtDate.getTime())) return gmtDate;
  }

  const hasExplicitZone =
    /[zZ]$/.test(raw)
    || /[+-]\d{2}:?\d{2}$/.test(raw)
    || /[+-]\d{2}:\d{2}$/.test(raw);

  if (hasExplicitZone) {
    const zoned = new Date(raw);
    if (!Number.isNaN(zoned.getTime())) return zoned;
  }

  // "2026-09-07T21:00:00" or "2026-09-07 21:00:00" → treat as UTC
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    let normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
    if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
      normalized = `${normalized}T00:00:00Z`;
    } else if (!/[zZ]$/.test(normalized) && !/[+-]\d{2}:?\d{2}$/.test(normalized)) {
      normalized = `${normalized}Z`;
    }
    const isoDate = new Date(normalized);
    if (!Number.isNaN(isoDate.getTime())) return isoDate;
  }

  const fallback = new Date(raw);
  if (!Number.isNaN(fallback.getTime())) return fallback;
  return null;
}

/**
 * Readable kick-off in the viewer's local timezone.
 * Example: "Wed, 31 Dec 2025, 02:00 AM GMT+5"
 */
export function formatLocalMatchDate(matchDate?: string): string {
  const date = parseUtcMatchDate(matchDate);
  if (!date) return matchDate ? String(matchDate).trim() : '';

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return date.toLocaleString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
    timeZoneName: 'short',
  });
}

export type StaticMatchPhase = 'upcoming' | 'LIVE' | 'FULL TIME' | '';

/** One-shot status from kick-off time — no live ticking. */
export function getStaticMatchPhase(matchDate?: string): StaticMatchPhase {
  const date = parseUtcMatchDate(matchDate);
  if (!date) return '';

  const diff = date.getTime() - Date.now();
  if (diff > 0) return 'upcoming';
  if (diff + FULL_MATCH_DURATION_MS < 0) return 'FULL TIME';
  return 'LIVE';
}

/**
 * Milliseconds until kick-off (positive) or since kick-off (negative).
 * Returns null when the date is missing/invalid.
 */
export function getTimeDifference(matchDate: string): number | null {
  const date = parseUtcMatchDate(matchDate);
  if (!date) return null;
  return date.getTime() - Date.now();
}
